import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import HeadingOne from '../components/HeadingOne';

import Nick5782Small from '../assets/images/nick-5782-600w.avif';
import Nick5782Medium from '../assets/images/nick-5782-1200w.avif';
import Nick5782Large from '../assets/images/nick-5782-2000w.avif';
import Nick5565Small from '../assets/images/nick-5565-600w.avif';
import Nick5565Medium from '../assets/images/nick-5565-1200w.avif';
import Nick5565Large from '../assets/images/nick-5565-2000w.avif';

const expertise = [
    'PhD-kandidaat Humane Biologie (Maastricht University)',
    'Master in Bewegingswetenschappen',
    'Bachelor Sportkunde',
    'Post-hbo Sportdiëtist',
    'Docent Trainingsleer & Inspanningsfysiologie (HAN)',
    'ISAK Level 3 Antropometrist',
];

const AboutPage = () => {
    useEffect(() => {
        document.title = `Over Nick - Private Gym Schijndel`;
    }, []);

    return (
        <>
            <section className="about-hero">
                <div className="default-container flex justify-center items-center h-full w-full">
                    <div className="text-center">
                        <HeadingOne>Nick van Schijndel.</HeadingOne>
                        <h3 className="text-primary-500 pt-2 pb-16">Trainer, onderzoeker en oprichter van Private Gym Schijndel.</h3>
                    </div>
                </div>
            </section>

            {/* Wie ben ik */}
            <section className="bg-white">
                <div className="default-container">
                    <div className="flex flex-col md:flex-row gap-12 md:gap-16 items-start">
                        <div className="w-full md:w-2/5 shrink-0">
                            <div className="rounded shadow-xl overflow-hidden zoom">
                                <img
                                    src={Nick5782Large}
                                    srcSet={`${Nick5782Small} 600w, ${Nick5782Medium} 1200w, ${Nick5782Large} 2000w`}
                                    sizes="(max-width: 768px) 100vw, 40vw"
                                    alt="Nick van Schijndel"
                                    className="object-cover w-full h-full"
                                    loading="eager"
                                />
                            </div>
                        </div>
                        <div className="w-full md:w-3/5">
                            <p className="text-xs font-semibold tracking-widest uppercase text-primary-500 pb-3">Wie ben ik?</p>
                            <h2 className="text-3xl md:text-5xl tracking-wider pb-4">Mijn naam is Nick van Schijndel.</h2>
                            <hr className="primary-hr mb-8" />
                            <p className="pb-6">Ik geloof dat een sterk lichaam de basis vormt voor een gezond, actief en zelfstandig leven.</p>
                            <p>Binnen Private Gym Schijndel help ik mensen bouwen aan een sterker, fitter en belastbaarder lichaam door middel van doelgerichte krachttraining, persoonlijke trainingsplannen en een rustige trainingsomgeving.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Mijn visie */}
            <section className="bg-black">
                <div className="default-container text-white">
                    <div className="max-w-2xl">
                        <p className="text-xs font-semibold tracking-widest uppercase text-primary-400 pb-3">Mijn visie</p>
                        <h2 className="text-3xl md:text-5xl tracking-wider text-white pb-4">Duurzame resultaten, geen snelle oplossingen.</h2>
                        <hr className="primary-hr mb-8" />
                        <p className="text-gray-300 pb-6">Veel mensen willen sterker, fitter en gezonder worden, maar vinden het lastig om structuur, begeleiding en een trainingsomgeving te vinden die bij hen past.</p>
                        <p className="text-gray-300 pb-6">Ik geloof niet in snelle oplossingen, hypes of tijdelijke uitdagingen. Duurzame resultaten ontstaan door consistent te trainen, voldoende te herstellen en stap voor stap sterker te worden.</p>
                        <p className="text-gray-300">Daarom staat krachttraining centraal binnen Private Gym Schijndel.</p>
                    </div>
                </div>
            </section>

            {/* Mijn achtergrond */}
            <section className="bg-white">
                <div className="default-container">
                    <div className="flex flex-col md:flex-row gap-12 md:gap-16 items-start">
                        <div className="w-full md:w-3/5">
                            <p className="text-xs font-semibold tracking-widest uppercase text-primary-500 pb-3">Mijn achtergrond</p>
                            <h2 className="text-3xl md:text-5xl tracking-wider pb-4">Praktijk, onderwijs en wetenschap.</h2>
                            <hr className="primary-hr mb-8" />
                            <p className="pb-6">Mijn visie is gebaseerd op een combinatie van praktijkervaring, onderwijs en wetenschappelijk onderzoek.</p>
                            <p className="pb-6">Naast mijn werkzaamheden binnen Private Gym Schijndel ben ik actief als docent en onderzoeker binnen de vakgebieden training, voeding en gezondheid.</p>
                            <p>Mijn promotieonderzoek aan Maastricht University richt zich op de rol van training en voeding bij het verbeteren van spiermassa, gezondheid en fysiek functioneren. Daarbij staat één vraag centraal: hoe kunnen we mensen helpen om sterker, fitter en beter belastbaar te worden gedurende hun leven? Deze combinatie van praktijk, onderwijs en wetenschappelijk onderzoek vormt de basis van de aanpak binnen Private Gym Schijndel.</p>
                        </div>
                        <div className="w-full md:w-2/5 shrink-0">
                            <div className="rounded shadow-xl overflow-hidden zoom h-96 md:h-full md:min-h-80">
                                <img
                                    src={Nick5565Large}
                                    srcSet={`${Nick5565Small} 600w, ${Nick5565Medium} 1200w, ${Nick5565Large} 2000w`}
                                    sizes="(max-width: 768px) 100vw, 40vw"
                                    alt="Nick van Schijndel"
                                    className="object-cover w-full h-full"
                                    loading="lazy"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Opleiding & expertise */}
            <section className="bg-gray-100">
                <div className="default-container">
                    <p className="text-xs font-semibold tracking-widest uppercase text-primary-500 pb-3">Opleiding & expertise</p>
                    <hr className="border-gray-300 mb-8" />
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-3">
                        {expertise.map((item, i) => (
                            <li key={i} className="flex items-start gap-3 text-gray-500 text-sm tracking-wide">
                                <span className="text-primary-400 shrink-0 mt-0.5">—</span>
                                <span>{item}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-white">
                <div className="default-container text-center flex flex-col justify-center items-center">
                    <h2>Klaar om te beginnen?</h2>
                    <p className="pt-4 pb-12">Neem contact op voor een vrijblijvend kennismakingsgesprek.</p>
                    <Link className="primary-button" to={'/contact'}>Neem contact op</Link>
                </div>
            </section>
        </>
    );
};

export default AboutPage;
